import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { sidebarItemsFor } from "./navItems";
import DsIcon from "./DsIcon";
import ThemeToggle from "../components/ThemeToggle";
import useTheme from "../hooks/useTheme";
import { useRole, useAccess, useDeny } from "../auth/AuthContext";
import { AUTH_ENABLED, currentEmail, signOut } from "../auth/session";

/**
 * DsSidebar — 데스크톱 좌측 사이드바 (md 이상).
 *
 * 구조(m4 톤): 상단 로고/타이틀 / 가운데 nav / 하단 테마 토글.
 * 그림자 0, md:border-r(ds-line), bg ds-canvas-soft. 라운드 4px.
 * 활성 항목 = ds-canvas-card + border + ink. 비활성 = mute, hover 시 ink.
 */
function isActive(pathname, search, item) {
  if (item.deeplink) return false;
  if (item.path === "/") return pathname === "/";
  return pathname === item.path || pathname.startsWith(item.path + "/");
}

export default function DsSidebar() {
  const { pathname, search } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const role = useRole();
  const items = sidebarItemsFor(role, useAccess(), useDeny());

  return (
    <aside
      className="hidden lg:flex lg:flex-col lg:w-64 lg:shrink-0 lg:border-r lg:h-screen lg:sticky lg:top-0"
      style={{
        background: "var(--ds-canvas-soft)",
        borderColor: "var(--ds-line)",
      }}
    >
      {/* 로고 + 타이틀 — 같은 폰트 크기라 items-center 로도 베이스라인이 맞고,
          h-16 안에서 수직 중앙(위로 붙어 보이던 문제, 사장 지적 2026-07-24) */}
      <div className="flex items-center gap-1.5 px-5 h-16 shrink-0" style={{ fontFamily: "var(--ff-brand)" }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--ds-ink)", letterSpacing: "-0.03em" }}>
          ig
        </span>
        <span style={{ fontWeight: 500, fontSize: 15, color: "var(--ds-mute)", letterSpacing: "0.01em" }}>
          works
        </span>
      </div>

      {/* nav */}
      <nav className="flex-1 flex flex-col gap-1 px-3 py-2 overflow-y-auto">
        {items.map((item, i) => {
          const active = isActive(pathname, search, item);
          const cls = "flex items-center gap-3 px-3 rounded";
          // 업무 흐름 묶음 구분선 — 역할 필터 뒤의 '보이는 앞 항목'과 비교하므로
          // 그룹이 통째로 숨겨져도(예: 직원에게 영업) 빈 구분선이 남지 않는다.
          const divider = i > 0 && item.group && items[i - 1].group !== item.group ? (
            <div aria-hidden
                 style={{ height: 1, background: "var(--ds-line)", opacity: 0.7, margin: "5px 12px" }} />
          ) : null;
          // Fragment 는 DOM 노드를 만들지 않아 구분선·항목이 그대로 flex 자식이 된다.
          const wrap = (node) => <Fragment key={item.key}>{divider}{node}</Fragment>;
          const style = {
            height: 40,
            fontSize: 14,
            fontWeight: active ? 600 : 500,
            color: active ? "var(--ds-ink)" : "var(--ds-mute)",
            background: active ? "var(--ds-canvas-card)" : "transparent",
            border: "1px solid",
            borderColor: active ? "var(--ds-line)" : "transparent",
            transition: "color 120ms ease, background 120ms ease",
          };
          const inner = (<><DsIcon name={item.icon} size={18} /><span>{item.label}</span></>);
          // 임베드(SMBM): 본체 클릭=앱 내 라우트, ↗ 화살표=새 탭 팝업(분리 클릭).
          if (item.embed) {
            return wrap(
              <div className={cls} style={{ ...style, paddingRight: 8 }}>
                <Link to={item.path} className="flex items-center gap-3 flex-1 min-w-0"
                      style={{ color: "inherit", textDecoration: "none" }}>
                  {inner}
                </Link>
                <a href={item.url} target="_blank" rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()} title="새 탭에서 열기" aria-label={`${item.label} 새 탭에서 열기`}
                   style={{ marginLeft: "auto", padding: "2px 6px", borderRadius: 6, color: "inherit", opacity: 0.55, fontSize: 13, textDecoration: "none" }}>↗</a>
              </div>
            );
          }
          if (item.external) {
            return wrap(
              <a href={item.url} target="_blank" rel="noopener noreferrer" className={cls} style={style}>
                {inner}
                <span aria-hidden style={{ marginLeft: "auto", fontSize: 12, opacity: 0.6 }}>↗</span>
              </a>
            );
          }
          return wrap(
            <Link to={item.path} className={cls} style={style}>
              {inner}
            </Link>
          );
        })}
      </nav>

      {/* 하단 계정(로그인 시) + 테마 토글 */}
      <div className="shrink-0" style={{ borderTop: "1px solid var(--ds-line)" }}>
        {AUTH_ENABLED && (
          <div className="px-3 pt-3">
            <div style={{ fontSize: 11.5, color: "var(--ds-mute)", padding: "0 8px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {currentEmail()}
            </div>
            <button
              type="button"
              onClick={() => signOut()}
              className="flex items-center gap-3 px-3 rounded"
              style={{ width: "100%", height: 38, fontSize: 13.5, fontWeight: 500,
                       color: "var(--ds-mute)", background: "transparent",
                       border: "1px solid transparent", cursor: "pointer" }}
            >
              <DsIcon name="logout" size={17} />
              <span>로그아웃</span>
            </button>
          </div>
        )}
        <div className="px-5 py-4 flex items-center">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </div>
      </div>
    </aside>
  );
}
